import { RowClassParams } from 'ag-grid-community';
import { capitalize } from 'lodash';
import { useCallback, useMemo } from 'react';

import { useAuthUser } from 'auth/hooks';
import { AgGridServerTable } from 'core/components';
import { AgGridFilterOption, SortDirection, TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider } from 'core/states';
import { AgGridGetRowsParams } from 'core/types';
import { AddPricingProfileButton } from 'pricing/components';
import { PricingProfilePricingType, PricingProfileType } from 'pricing/enums';
import { pricingProfileService } from 'pricing/services';
import { IPricingProfile } from 'pricing/types';

import { getColumnDef } from './columns';
import { PricingProfileStatus } from './enums';
import { RemoveSelectedProfiles } from './RemoveSelectedProfiles';
import { UpdateSelectedProfiles } from './UpdateSelectedProfiles';
import { getStatus } from './utils';

interface IProps {
  type: PricingProfilePricingType;
  contactGroupId?: number;
}

export const PricingProfileList = ({ type, contactGroupId }: IProps) => {
  const { user } = useAuthUser();

  const tableId = useMemo(() => {
    return `${type}PricingProfilesTable`;
  }, [type]);

  const entityType = useMemo(() => {
    return `${capitalize(type)}Profile`;
  }, [type]);

  const layoutType = useMemo(() => {
    return type === PricingProfilePricingType.PRICING
      ? TableLayoutType.PRICING_PROFILE
      : TableLayoutType.COSTING_PROFILE;
  }, [type]);

  const columnDefs = useMemo(() => getColumnDef({ type }), [type]);

  const initialState = useMemo(() => {
    return {
      filter: {
        filterModel: {
          corporateUserName: {
            filterType: 'text',
            type: AgGridFilterOption.CONTAINS,
            filter: `${user?.firstName} ${user?.lastName}`,
          },
        },
      },
      sort: {
        sortModel: [{ colId: 'updatedAt', sort: SortDirection.DESC }],
      },
    };
  }, [user?.firstName, user?.lastName]);

  const actionButtons = useMemo(() => {
    if (contactGroupId) {
      return [];
    }

    return [<AddPricingProfileButton key="addPricingProfileButton" type={type} />];
  }, [contactGroupId, type]);

  const selectionActionButtons = useMemo(() => {
    return [
      <UpdateSelectedProfiles key="updateSelectedProfilesButton" type={type} />,
      <RemoveSelectedProfiles key="removeSelectedProfilesButton" type={type} />,
    ];
  }, [type]);

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) =>
      pricingProfileService.getPricingProfiles({
        filters: {
          ...filters,
          status:
            Array.isArray(filters.status) && filters.status.length === 1
              ? filters.status[0]
              : undefined,
          pricingType: [type],
          type: [PricingProfileType.DEFAULT],
        },
        ...(contactGroupId && { viewBy: { contactGroup: contactGroupId } }),
        ...rest,
      }),
    [type, contactGroupId]
  );

  const getRowClass = useCallback((row: RowClassParams<IPricingProfile>) => {
    if (!row.data) {
      return '';
    }

    const status = getStatus(row.data);

    if (status === PricingProfileStatus.INACTIVE) {
      return 'disabledRow';
    }

    return '';
  }, []);

  return (
    <AgGridServerTableProvider
      key={user?.id}
      tableId={tableId}
      entityType={entityType}
      columnDefs={columnDefs}
      getRows={getRows}
      initialState={initialState}
      hasLayoutActions
      hasManageFilters
      layoutType={layoutType}
      getRowClass={getRowClass}
    >
      <AgGridServerTable
        actionButtons={actionButtons}
        selectionActionButtons={selectionActionButtons}
      />
    </AgGridServerTableProvider>
  );
};
